/**
 * CenterPanel — Document workspace / file preview area (center of Home).
 *
 * Layout:
 *  1. Toolbar — "WORKSPACE" label + vertical divider + active document name +
 *     file-type badge + open-in-new-tab (PDF) / download (other formats)
 *  2. Canvas — viewer routed by the file's canonical format:
 *     PDF → PdfViewer (white-themed pdf.js canvas viewer, fed the blob URL
 *     after split-document page-range slicing via pdf-lib); XLS/XLSX →
 *     ExcelViewer (SheetJS); DOCX → DocxViewer (docx-preview); DOC →
 *     DocxViewer fed with server-converted preview bytes
 *     (GET /files/{id}/preview-content — the browser can't render the legacy
 *     binary format natively); unknown → download-only fallback.
 *
 * Fetches content via filesApi.getFileContent(fileStorageId) and decodes the
 * base64 once — every viewer is a read-only consumer of those bytes (the
 * stored file is never converted or replaced, REQ-17; the .doc preview is a
 * derived in-memory payload, the original stays untouched).
 */
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { Download01, File06, LinkExternal01, Loading01 } from "@untitledui/icons";
import { PDFDocument } from "pdf-lib";
import { Badge } from "@/components/base/badges/badges";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icons";
import { useFilesApi } from "@/services/api/files-api";
import { ExceptionCodeEnum } from "@/types/api/common";
import type { DocumentDetailDto } from "@/types/api/documents";
import type { HomeDocument } from "@/types/home";
import { fileFormatLabel, resolveFileFormat, viewerKindFor, type ViewerKind } from "./viewers/file-format";
import { ViewerFallback } from "./viewers/viewer-fallback";

// Viewers are lazy-loaded so their libraries (pdf.js, SheetJS, docx-preview)
// stay out of the main bundle
const PdfViewer = lazy(() => import("./viewers/pdf-viewer").then((m) => ({ default: m.PdfViewer })));
const ExcelViewer = lazy(() => import("./viewers/excel-viewer").then((m) => ({ default: m.ExcelViewer })));
const DocxViewer = lazy(() => import("./viewers/docx-viewer").then((m) => ({ default: m.DocxViewer })));

interface CenterPanelProps {
    /** Currently active document (null when nothing is selected) */
    document: HomeDocument | null;
    /** Full document detail — provides pages.start/end for page extraction */
    documentDetail: DocumentDetailDto | null;
}

/** A fetched + decoded file, routed to its viewer. */
interface LoadedFile {
    fileId: number;
    kind: ViewerKind;
    /** Decoded file bytes — shared by viewers and the download fallback */
    bytes: Uint8Array<ArrayBuffer>;
    fileName: string;
    contentType: string;
    /** Toolbar badge label, e.g. "PDF", "XLSX" */
    formatLabel: string;
    /** Blob URL — PDF only (drives the PdfViewer + open-in-new-tab) */
    url?: string;
    /**
     * Server-converted render payload — legacy .doc only (converted .docx bytes
     * for the DOCX viewer). Undefined when conversion failed → download fallback.
     * Downloads always use `bytes` (the untouched original).
     */
    previewBytes?: Uint8Array<ArrayBuffer>;
}

/** Decode a base64 payload from the content APIs into raw bytes. */
function decodeBase64(content: string): Uint8Array<ArrayBuffer> {
    const binaryString = atob(content);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
}

export function CenterPanel({ document, documentDetail }: CenterPanelProps) {
    const filesApi = useFilesApi();
    const [loadedFile, setLoadedFile] = useState<LoadedFile | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const blobUrlRef = useRef<string | null>(null);

    // Fetch file content when document changes
    useEffect(() => {
        // Cleanup previous blob URL
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }
        setLoadedFile(null);

        // BizDuplicateFile: the uploaded file was rejected and isn't stored — show the existing
        // duplicate's file instead. All other errorCodes (e.g. BizVendorNotFound) leave the source
        // file intact, so load it normally.
        const isDuplicateFileError = document?.errorCode === ExceptionCodeEnum.BizDuplicateFile;
        const fileIdToLoad = isDuplicateFileError ? document?.duplicateFileId : document?.fileStorageId;

        if (!fileIdToLoad) {
            setIsLoading(false);
            setError(null);
            return;
        }

        let cancelled = false;

        const fetchFile = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const fileContent = await filesApi.getFileContent(fileIdToLoad);

                if (cancelled) return;

                // Base64 → Uint8Array (format-agnostic, shared by all viewers)
                const bytes = decodeBase64(fileContent.content);

                if (cancelled) return;

                // Route by canonical server-derived format (contentType/extension fallback
                // covers rows predating file-type validation)
                const format = resolveFileFormat(fileContent.fileType, fileContent.contentType, fileContent.fileName);
                const kind = viewerKindFor(format);

                const loaded: LoadedFile = {
                    fileId: fileIdToLoad,
                    kind,
                    bytes,
                    fileName: fileContent.fileName,
                    contentType: fileContent.contentType,
                    formatLabel: fileFormatLabel(format, fileContent.fileName),
                };

                if (kind === "doc") {
                    // Legacy .doc: the browser can't render the binary format — fetch the
                    // server-converted .docx payload for the DOCX viewer. Conversion failure
                    // (400/422/network) degrades to the download-only fallback, never an error.
                    try {
                        const preview = await filesApi.getFilePreviewContent(fileIdToLoad);
                        if (cancelled) return;
                        loaded.previewBytes = decodeBase64(preview.content);
                    } catch (previewErr) {
                        console.warn("Legacy .doc preview conversion unavailable:", previewErr);
                    }
                }

                if (kind === "pdf") {
                    // Extract specific pages if page range is available (same pattern as LegacyDocumentsPage).
                    // Page slicing is PDF-only by definition — Office files always map to one whole-file document.
                    // Skip page extraction when loading a duplicate file — the page range belongs to the failed
                    // document, not the duplicate's source file.
                    const hasPageRange = !isDuplicateFileError && documentDetail?.pages?.start != null && documentDetail?.pages?.end != null;

                    let url: string;
                    if (hasPageRange) {
                        const pdfDoc = await PDFDocument.load(bytes);
                        const newPdfDoc = await PDFDocument.create();

                        const pagesToCopy = [];
                        for (let i = documentDetail.pages.start; i <= documentDetail.pages.end; i++) {
                            pagesToCopy.push(i);
                        }

                        const copiedPages = await newPdfDoc.copyPages(pdfDoc, pagesToCopy);
                        copiedPages.forEach((page) => newPdfDoc.addPage(page));

                        const pdfBytes = await newPdfDoc.save();
                        const pdfArrayBuffer = pdfBytes.buffer.slice(pdfBytes.byteOffset, pdfBytes.byteOffset + pdfBytes.byteLength) as ArrayBuffer;
                        const blob = new Blob([pdfArrayBuffer], { type: "application/pdf" });
                        url = URL.createObjectURL(blob);
                    } else {
                        const blob = new Blob([bytes], { type: fileContent.contentType });
                        url = URL.createObjectURL(blob);
                    }

                    if (cancelled) {
                        URL.revokeObjectURL(url);
                        return;
                    }

                    blobUrlRef.current = url;
                    loaded.url = url;
                }

                setLoadedFile(loaded);
            } catch (err) {
                if (!cancelled) {
                    console.error("Failed to fetch file content:", err);
                    setError("There was an error loading the file.");
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        fetchFile();

        return () => {
            cancelled = true;
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
        };
    }, [document?.fileStorageId, document?.errorCode, document?.duplicateFileId, documentDetail?.pages?.start, documentDetail?.pages?.end, filesApi]);

    const handleOpenInNewTab = useCallback(() => {
        if (loadedFile?.url) window.open(loadedFile.url, "_blank");
    }, [loadedFile?.url]);

    // Download-original fallback: builds a blob + anchor from the already-fetched bytes (REQ-18).
    // NOTE: the `document` prop shadows the DOM global — use window.document here.
    const handleDownload = useCallback(() => {
        if (!loadedFile) return;
        const blob = new Blob([loadedFile.bytes], { type: loadedFile.contentType || "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        const anchor = window.document.createElement("a");
        anchor.href = url;
        anchor.download = loadedFile.fileName || "document";
        window.document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, [loadedFile]);

    return (
        <div className="relative flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-secondary">
            {/* ---- Toolbar ---- */}
            <div className="z-20 flex h-12 shrink-0 items-center justify-between border-b border-secondary bg-primary px-6">
                <div className="flex items-center gap-4">
                    <span className="text-xs font-semibold tracking-wider text-quaternary uppercase">Workspace</span>
                    {document && (
                        <>
                            <div className="h-4 w-px bg-secondary" />
                            <span className="max-w-[300px] truncate text-sm font-medium text-secondary">{document.vendorName}</span>
                            {loadedFile && (
                                <Badge size="sm" color="gray">
                                    {loadedFile.formatLabel}
                                </Badge>
                            )}
                        </>
                    )}
                </div>
                {document &&
                    loadedFile &&
                    (loadedFile.kind === "pdf" ? (
                        <ButtonUtility icon={LinkExternal01} size="xs" color="tertiary" tooltip="Open in new tab" onClick={handleOpenInNewTab} />
                    ) : (
                        <ButtonUtility icon={Download01} size="xs" color="tertiary" tooltip="Download original" onClick={handleDownload} />
                    ))}
            </div>

            {/* ---- Canvas ---- */}
            {/* Edge-to-edge: viewers own their scrolling and fill the panel; the
                empty/loading/error/fallback states bring their own padded wrapper */}
            <div className="flex flex-1 overflow-hidden">
                {!document ? (
                    <EmptyState />
                ) : isLoading ? (
                    <LoadingState />
                ) : error ? (
                    <ErrorState />
                ) : loadedFile?.kind === "pdf" && loadedFile.url ? (
                    <Suspense fallback={<LoadingState />}>
                        {/* Keyed on the blob URL: remounts on document switch AND page-range change */}
                        <PdfViewer key={loadedFile.url} url={loadedFile.url} fileName={loadedFile.fileName} onDownload={handleDownload} />
                    </Suspense>
                ) : loadedFile?.kind === "excel" ? (
                    <Suspense fallback={<LoadingState />}>
                        <ExcelViewer key={loadedFile.fileId} bytes={loadedFile.bytes} fileName={loadedFile.fileName} onDownload={handleDownload} />
                    </Suspense>
                ) : loadedFile?.kind === "docx" ? (
                    <Suspense fallback={<LoadingState />}>
                        <DocxViewer key={loadedFile.fileId} bytes={loadedFile.bytes} fileName={loadedFile.fileName} onDownload={handleDownload} />
                    </Suspense>
                ) : loadedFile?.kind === "doc" && loadedFile.previewBytes ? (
                    // Legacy .doc renders through the same DOCX viewer using the
                    // server-converted preview payload (the stored original is untouched)
                    <Suspense fallback={<LoadingState />}>
                        <DocxViewer key={loadedFile.fileId} bytes={loadedFile.previewBytes} fileName={loadedFile.fileName} onDownload={handleDownload} />
                    </Suspense>
                ) : loadedFile?.kind === "doc" ? (
                    <ViewerFallback
                        title="Preview not available"
                        message="A preview couldn't be generated for this legacy Word (.doc) file. Download the original to view it."
                        fileName={loadedFile.fileName}
                        onDownload={handleDownload}
                    />
                ) : loadedFile ? (
                    <ViewerFallback
                        title="Preview not available"
                        message="This file type can't be previewed in the browser. Download the original to view it."
                        fileName={loadedFile.fileName}
                        onDownload={handleDownload}
                    />
                ) : (
                    <DocumentPreviewState document={document} />
                )}
            </div>
        </div>
    );
}

function EmptyState() {
    return (
        <div className="custom-scrollbar flex h-full w-full items-start justify-center overflow-y-auto p-6">
            <div className="flex aspect-[1/1.414] w-full max-w-4xl flex-col items-center justify-center gap-4 rounded-lg border border-secondary bg-primary shadow-xl">
                <FeaturedIcon icon={File06} color="gray" theme="light" size="xl" />
                <div className="text-center">
                    <p className="text-sm font-semibold tracking-wider text-tertiary uppercase">No Document Selected</p>
                    <p className="mt-1 text-xs text-quaternary">Select a document from the left panel to preview it here</p>
                </div>
            </div>
        </div>
    );
}

function DocumentPreviewState({ document }: { document: HomeDocument }) {
    return (
        <div className="custom-scrollbar flex h-full w-full items-start justify-center overflow-y-auto p-6">
            <div className="flex aspect-[1/1.414] w-full max-w-4xl flex-col items-center justify-center gap-4 rounded-lg border border-secondary bg-primary shadow-xl">
                <FeaturedIcon icon={File06} color="gray" theme="light" size="xl" />
                <div className="text-center">
                    <p className="text-sm font-semibold tracking-wider text-tertiary uppercase">Document Preview</p>
                    <p className="mt-1 text-xs text-quaternary">
                        {document.vendorName} &middot; {document.documentType}
                    </p>
                </div>
            </div>
        </div>
    );
}

function LoadingState() {
    return (
        <div className="flex h-full w-full items-center justify-center p-8 text-center">
            <div>
                <Loading01 className="mx-auto size-8 animate-spin text-fg-quaternary" />
                <p className="mt-4 text-sm text-tertiary">Loading document...</p>
            </div>
        </div>
    );
}

function ErrorState() {
    return (
        <div className="flex h-full w-full items-center justify-center p-8 text-center">
            <div>
                <FeaturedIcon icon={File06} color="error" theme="light" size="lg" className="mx-auto" />
                <p className="mt-4 text-sm text-tertiary">There was an error loading the file.</p>
            </div>
        </div>
    );
}
